import React, { useEffect, useState } from "react";
import api from "../../lib/api";
import { Cpu, DollarSign, Timer, MessageSquare } from "lucide-react";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
  LineChart, Line,
} from "recharts";

const fmtUSD = n => "$" + Number(n).toFixed(n < 1 ? 4 : 2);

export default function AiUsageMonitor() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);

  useEffect(() => { api.get("/admin/ai-usage", { params: { days } }).then(r => setData(r.data)); }, [days]);

  if (!data) return <div className="p-6 text-ink-soft">Loading…</div>;
  const s = data.summary;

  return (
    <div className="p-6 space-y-5" data-testid="ai-usage-root">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="section-label">Admin · AI Usage</div>
          <h1 className="text-2xl font-bold tracking-tight mt-1">AI Usage Monitor</h1>
          <p className="text-sm text-ink-soft">
            Model: <span className="font-mono">{s.model}</span> · Pricing <span className="font-mono">${s.pricing.input_per_mtok_usd}/MTok input · ${s.pricing.output_per_mtok_usd}/MTok output</span>.
          </p>
        </div>
        <select data-testid="ai-days-select" value={days} onChange={e=>setDays(Number(e.target.value))} className="border border-border rounded-md px-3 py-2 text-sm">
          {[7, 14, 30, 60, 90].map(d => <option key={d} value={d}>Last {d} days</option>)}
        </select>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { l: "Total calls", v: s.total_calls.toLocaleString(), i: MessageSquare, a:"bg-brand" },
          { l: "Cost (USD)", v: fmtUSD(s.total_cost_usd), i: DollarSign, a:"bg-status-success" },
          { l: "Tokens used", v: (s.total_input_tokens + s.total_output_tokens).toLocaleString(), i: Cpu, a:"bg-status-info" },
          { l: "Avg latency", v: s.avg_latency_ms ? `${s.avg_latency_ms} ms` : "—", i: Timer, a:"bg-status-warning" },
        ].map(m => {
          const Icon = m.i;
          return (
            <div key={m.l} className="card p-4" data-testid={`ai-tile-${m.l.toLowerCase().replace(/\s/g,'-')}`}>
              <div className="flex items-center justify-between">
                <div className="section-label">{m.l}</div>
                <div className={"h-7 w-7 rounded-md text-white flex items-center justify-center " + m.a}><Icon size={14}/></div>
              </div>
              <div className="metric-value">{m.v}</div>
            </div>
          );
        })}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="card p-4 lg:col-span-2">
          <div className="section-label">Daily cost trend</div>
          <div className="h-[260px] mt-3">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data.by_day}>
                <CartesianGrid stroke="#E2E8F0" vertical={false}/>
                <XAxis dataKey="day" tick={{fontSize:10, fill:'#64748B'}} tickFormatter={d => d.slice(5)} axisLine={false} tickLine={false}/>
                <YAxis yAxisId="cost" tick={{fontSize:10, fill:'#64748B'}} axisLine={false} tickLine={false} width={60}/>
                <YAxis yAxisId="calls" orientation="right" tick={{fontSize:10, fill:'#64748B'}} axisLine={false} tickLine={false} width={40}/>
                <Tooltip />
                <Legend wrapperStyle={{fontSize: 11}}/>
                <Line yAxisId="cost" type="monotone" dataKey="cost_usd" stroke="#064E3B" strokeWidth={2} dot={{r:2}} name="Cost (USD)"/>
                <Line yAxisId="calls" type="monotone" dataKey="calls" stroke="#3B82F6" strokeWidth={1.5} dot={false} name="Calls"/>
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="card p-4">
          <div className="section-label">By endpoint</div>
          <table className="dense mt-2">
            <thead><tr><th>Endpoint</th><th className="text-right">Calls</th><th className="text-right">Cost</th></tr></thead>
            <tbody>
              {data.by_endpoint.map(e => (
                <tr key={e.endpoint}>
                  <td className="font-mono text-xs">{e.endpoint}</td>
                  <td className="num">{e.calls}</td>
                  <td className="num">{fmtUSD(e.cost_usd)}</td>
                </tr>
              ))}
              {data.by_endpoint.length === 0 && <tr><td colSpan={3} className="text-center py-4 text-ink-soft">No AI calls yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      <div className="card overflow-hidden">
        <div className="px-4 py-3 border-b border-border section-label">Recent calls</div>
        <table className="dense">
          <thead><tr><th>When</th><th>User</th><th>Endpoint</th><th className="text-right">In</th><th className="text-right">Out</th><th className="text-right">Cost</th><th className="text-right">Latency</th><th>Status</th></tr></thead>
          <tbody>
            {data.recent.map(r => (
              <tr key={r.id} data-testid={`ai-row-${r.id}`}>
                <td className="font-mono text-xs">{r.ts?.slice(0,19).replace('T',' ')}</td>
                <td className="text-xs">{r.user_email}</td>
                <td className="font-mono text-xs">{r.endpoint}</td>
                <td className="num">{r.input_tokens}</td>
                <td className="num">{r.output_tokens}</td>
                <td className="num">{fmtUSD(r.est_cost_usd)}</td>
                <td className="num">{r.latency_ms} ms</td>
                <td>
                  {r.error ? <span className="pill pill-error">{r.error}</span> :
                   r.short_circuit ? <span className="pill">no-llm</span> :
                   <span className="pill pill-success">ok</span>}
                </td>
              </tr>
            ))}
            {data.recent.length === 0 && <tr><td colSpan={8} className="text-center py-6 text-ink-soft">No AI calls in this window.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
